import {
  BackgammonAdapter,
  type BackgammonCube,
  type BackgammonDice,
  type BackgammonEndpoint,
  type BackgammonGame,
  type BackgammonResult,
  type BackgammonStep,
  type BackgammonTurn,
  backgammonStartPosition,
  canOfferDouble,
  cubeOf,
  openingTurnOf,
  stakeMultiplier,
} from '@chessmate/game-engine';
import type { DieRoller } from '@chessmate/game-engine';
import type {
  BackgammonSnapshot,
  BackgammonTurnRecord,
  GameSnapshot,
  LiveClock,
  LiveEvent,
  Turn,
} from '@chessmate/protocol';
import { create } from 'zustand';
import type { AppQuery } from '../lib/query.js';
import type { RobotTurnRequest } from './robot-turn.js';
import { pickRobotTurn } from './robot-turn.js';
import { requestRobotTurn } from './robot-worker-client.js';
import { ApiError, api } from '../services/api.js';
import { type LiveClient, connectLive } from '../services/live.js';
import { playSfx, setAmbientMood } from '../services/audio.js';

/**
 * The backgammon store — deliberately a SEPARATE store from `game-store.ts`,
 * not a branch inside it. `game-store` is chess-shaped end to end: FEN
 * snapshots, `tap(square)`, a `MoveEngine`, shield maps. Backgammon's state
 * (`BackgammonGame { position, dice }`) fits none of it, and the wiring
 * proposal (docs/proposals/backgammon-wiring.md, slice 3) promised a dedicated
 * screen rather than an adapter squeeze. Two stores also means the chess game
 * cannot regress from anything backgammon does.
 *
 * **The turn flow, and why `candidates` exists.** The engine speaks in
 * *complete* dice plays (`BackgammonTurn`), but a kid plays *step by step*:
 * tap a checker, tap where it goes, see the next die. So the store keeps the
 * roll's `candidates` (every legal turn) and narrows them as steps are tapped.
 * A tap on a source is legal iff some candidate's next-unplayed step starts
 * there; a tap on a destination narrows further. When the narrowed set is down
 * to exactly one fully-played turn, the turn commits. This makes an illegal
 * sequence unrepresentable — there is never a "commit" button to press at the
 * wrong moment — and the dice obligation (maximal play, higher-die rule) is
 * enforced by the engine's own candidate list rather than re-implemented here.
 */

export type BackgammonPhase = 'idle' | 'playing' | 'gameover';

/** Present while an online session is attached — the store is then
 *  server-authoritative: dice come from live:roll, turns submit to the API,
 *  undo/restart are unavailable. Design: docs/proposals/backgammon-online.md. */
export interface BackgammonOnlineSession {
  readonly token: string;
  readonly gameId: string;
  readonly side: Turn;
  /** Read-only spectator view (additive 2026-10-05): every play action is a
   *  no-op and the screen hides its controls. */
  readonly spectating: boolean;
}

interface BackgammonStore {
  readonly adapter: BackgammonAdapter;
  game: BackgammonGame | null;
  /** Snapshots taken before each of the player's rolls — `undo()` pops one. */
  history: BackgammonGame[];
  /** The roll's legal turns, narrowed as steps are tapped. */
  candidates: BackgammonTurn[];
  /** Steps committed within the current turn (partial play). */
  played: BackgammonStep[];
  selected: BackgammonEndpoint | null;
  /** Destinations reachable from `selected` by some candidate's next step. */
  targets: BackgammonEndpoint[];
  phase: BackgammonPhase;
  robotThinking: boolean;
  /** The full engine result — kind carries the gammon vocabulary the coach
   *  needs ("you won doubly!"), not just who won. */
  result: BackgammonResult | null;
  /** Set once, from the query, for deterministic audit/e2e rolls. */
  _roller: DieRoller | null;
  /** Robot search depth: undefined = greedy (the beatable default), 1 = the
   *  annealed expectimax tier. Applies from the next robot reply. */
  robotDepth: 1 | 2 | undefined;
  /** Online session — null in the offline robot game. */
  online: BackgammonOnlineSession | null;
  /** A turn/roll POST is in flight; taps and buttons wait. */
  submitting: boolean;
  onlineError: string | null;
  /** Server says it is the opponent's move (online only). */
  waiting: boolean;
  /** Server-authoritative clock (online only; null until the first live
   *  event carries one — un-clocked legacy games never get one). */
  clock: LiveClock | null;

  start(query: AppQuery): void;
  /** Set the robot's search depth (1 = annealed expectimax; undefined back
   *  to greedy). Survives restart; applies from the next robot reply. */
  setRobotDepth(depth: 1 | 2 | undefined): void;
  /** Attach an online session seeded from the server's GameSnapshot. */
  openOnline(input: {
    token: string;
    gameId: string;
    side: Turn;
    game: GameSnapshot;
    spectating?: boolean;
  }): void;
  roll(): void;
  /** Doubling cube (online only, additive 2026-10-05): offer a double
   *  instead of rolling; take/pass answer the opponent's offer. All three
   *  are server round-trips — the response snapshot is the truth. */
  offerDouble(): void;
  takeCube(): void;
  passCube(): void;
  /** A tap on a point (1–24), the bar, or the off tray. Meaning depends on
   *  whether a source is already selected. */
  tap(endpoint: BackgammonEndpoint): void;
  undo(): void;
  restart(): void;
  /** Leaving the screen: park the game at idle so a stale board never
   *  re-appears behind a half-finished BackgammonScreen render guard. */
  stop(): void;
}

function rollerFrom(query: AppQuery): DieRoller {
  const pairs = query.dice
    .map((raw) => raw.split('-').map((n) => Number(n)))
    .filter(
      (pair): pair is [number, number] => pair.length === 2 && pair.every((n) => n >= 1 && n <= 6),
    );
  if (pairs.length === 0) return Math.random;
  // Scripted: each roll consumes two draws (roll() calls the die source
  // twice), cycling through the script.
  let i = 0;
  return () => {
    const pair = pairs[Math.floor(i / 2) % pairs.length];
    const value = pair?.[i % 2];
    i += 1;
    if (value === undefined) return Math.random();
    return (value - 1) / 6 + 0.001;
  };
}

/** The tournament opening, offline too (2026-10-05, matching online):
 *  one die per side, higher plays first with both. Ties re-roll — the
 *  scripted `?dice=` cycler makes that transparent (every draw is the same
 *  value, so a tie just burns draws, and the flow scripts pick non-tie
 *  pairs). The opening consumes exactly two roller draws, the same economy
 *  as a plain roll, which is why the deterministic flows survive. */
function rollOpening(roller: DieRoller): { dice: BackgammonDice; turn: Turn } {
  const die = () => 1 + Math.floor(roller() * 6);
  let d1 = die();
  let d2 = die();
  let turn = openingTurnOf(d1, d2);
  while (turn === null) {
    d1 = die();
    d2 = die();
    turn = openingTurnOf(d1, d2);
  }
  return { dice: [d1, d2] as [number, number], turn };
}

// --- Online session plumbing (module-level; one live session at a time) ---

let liveClient: LiveClient | null = null;

function disconnectLive(): void {
  liveClient?.close();
  liveClient = null;
}

/** Convert a wire snapshot into the store's BackgammonGame shape. The cube
 *  normalizes through `cubeOf` — pre-deploy in-flight games carry no cube
 *  fields, and every reader gets centered-at-1 instead of undefined. */
function gameOf(snap: BackgammonSnapshot): BackgammonGame {
  return {
    position: {
      points: [...snap.points],
      bar: { ...snap.bar },
      off: { ...snap.off },
      turn: snap.turn,
    },
    dice: snap.dice ? ([...snap.dice] as [number, number]) : null,
    cube: cubeOf(snap.cube),
    cubeOffer: snap.cubeOffer ?? null,
  };
}

/** Adopt an authoritative server snapshot: reset the narrowing, recompute
 *  candidates when it is our roll to play. Idempotent — the submitting
 *  client and every subscriber receive the same state. */
function adoptServerGame(server: GameSnapshot | BackgammonSnapshot): void {
  const state = useBackgammonStore.getState();
  const online = state.online;
  if (!online) return;
  const snap: BackgammonSnapshot =
    'backgammon' in server && server.backgammon
      ? server.backgammon
      : (server as BackgammonSnapshot);
  const game = gameOf(snap);
  const myTurn = snap.turn === online.side;
  const candidates =
    myTurn && !online.spectating && game.dice ? state.adapter.legalTurns(game) : [];
  const finished = serverHasResult(server);
  useBackgammonStore.setState({
    game,
    candidates,
    played: [],
    selected: null,
    targets: [],
    waiting: !myTurn && !finished,
    submitting: false,
    phase: finished ? 'gameover' : 'playing',
    ...(finished ? { result: resultOf(server) } : {}),
  });
  // Dead roll — pass automatically, exactly like the offline store's beat.
  // A spectator never submits anything, not even a forced pass.
  if (myTurn && !online.spectating && game.dice && candidates.length === 0) {
    void submitPass(game);
  }
}

function serverHasResult(server: GameSnapshot | BackgammonSnapshot): boolean {
  return 'status' in server && server.status === 'completed';
}

function resultOf(server: GameSnapshot | BackgammonSnapshot): BackgammonResult | null {
  if (!('result' in server) || !server.result) return null;
  const r = server.result;
  if (r.kind === 'gammon' || r.kind === 'backgammon' || r.kind === 'elimination') {
    return { kind: r.kind, winner: r.winner };
  }
  // resign/timeout on a backgammon game read as a plain win for the winner.
  return 'winner' in r ? { kind: 'elimination', winner: r.winner } : null;
}

async function submitPass(game: BackgammonGame): Promise<void> {
  const state = useBackgammonStore.getState();
  const online = state.online;
  if (!online || !game.dice) return;
  useBackgammonStore.setState({ submitting: true });
  try {
    const server = await api.submitBackgammonTurn(online.token, online.gameId, {
      dice: [...game.dice] as [number, number],
      steps: [],
    });
    adoptServerGame(server);
  } catch (err) {
    useBackgammonStore.setState({
      submitting: false,
      onlineError: err instanceof ApiError ? err.message : 'pass-failed',
    });
  }
}

/** Shared online round-trip for a cube action — the response snapshot is the
 *  truth, so adopt it exactly like a roll/turn response. */
async function cubeAction(action: 'double' | 'take' | 'pass'): Promise<void> {
  const state = useBackgammonStore.getState();
  const online = state.online;
  if (!online || online.spectating || state.phase !== 'playing' || state.submitting) return;
  useBackgammonStore.setState({ submitting: true });
  try {
    adoptServerGame(await api.cubeBackgammon(online.token, online.gameId, action));
  } catch (err) {
    useBackgammonStore.setState({
      submitting: false,
      onlineError: err instanceof ApiError ? err.message : 'cube-failed',
    });
  }
}

/** One async LiveEvent dispatcher — kept sync-typed at the callsite. */
async function onLiveEvent(event: LiveEvent): Promise<void> {
  const online = useBackgammonStore.getState().online;
  if (!online) return;
  if (event.type === 'live:clock') {
    useBackgammonStore.setState({ clock: event.clock });
    return;
  }
  if (event.type === 'live:snapshot') {
    useBackgammonStore.setState({ clock: event.clock });
    adoptServerGame(event.game);
    return;
  }
  if (event.type === 'live:roll' || event.type === 'live:move' || event.type === 'live:cube') {
    // Adopt-last-wins: the submitting client's HTTP response and its own
    // live event carry the same snapshot, so a double adopt is a no-op.
    if (event.type === 'live:move' && !event.snapshot) return;
    // live:move carries the post-action clock; live:roll/live:cube moved no
    // clock, so the display keeps its previous value there.
    if (event.type === 'live:move') {
      useBackgammonStore.setState({ clock: event.clock });
    }
    adoptServerGame(event.snapshot!);
  }
  if (event.type === 'live:end') {
    const r = event.result;
    const result =
      r.kind === 'gammon' || r.kind === 'backgammon' || r.kind === 'elimination'
        ? { kind: r.kind, winner: r.winner }
        : 'winner' in r
          ? { kind: 'elimination' as const, winner: r.winner }
          : null;
    useBackgammonStore.setState({
      phase: 'gameover',
      waiting: false,
      submitting: false,
      ...(result ? { result } : {}),
    });
  }
}

export const useBackgammonStore = create<BackgammonStore>((set, get) => ({
  adapter: new BackgammonAdapter(),
  game: null,
  history: [],
  candidates: [],
  played: [],
  selected: null,
  targets: [],
  phase: 'idle',
  robotThinking: false,
  result: null,
  _roller: null,
  robotDepth: undefined,
  online: null,
  submitting: false,
  onlineError: null,
  waiting: false,
  clock: null,

  start(query: AppQuery) {
    setAmbientMood('backgammon');
    const roller = rollerFrom(query);
    const adapter = new BackgammonAdapter(roller);
    const opening = rollOpening(roller);
    const game: BackgammonGame = {
      position: backgammonStartPosition(opening.turn),
      dice: opening.dice,
    };
    set({
      adapter,
      game,
      history: [],
      candidates: opening.turn === 'w' ? adapter.legalTurns(game) : [],
      played: [],
      selected: null,
      targets: [],
      phase: 'playing',
      robotThinking: false,
      result: null,
      _roller: roller,
      online: null,
      submitting: false,
      onlineError: null,
      waiting: false,
      clock: null,
    });
    disconnectLive();
    // Black won the opening roll — the robot plays it, after the beat.
    if (opening.turn === 'b') void robotReply();
  },

  setRobotDepth(depth) {
    set({ robotDepth: depth });
  },

  openOnline(input) {
    const session = {
      token: input.token,
      gameId: input.gameId,
      side: input.side,
      spectating: input.spectating ?? false,
    };
    set({
      online: session,
      phase: 'playing',
      robotThinking: false,
      submitting: false,
      onlineError: null,
      result: null,
      history: [],
      _roller: null,
      waiting: input.game.turn !== input.side,
      clock: null,
    });
    adoptServerGame(input.game);
    disconnectLive();
    liveClient = connectLive(input.gameId, {
      token: input.token,
      onEvent: (event: LiveEvent) => void onLiveEvent(event),
    });
  },

  roll() {
    const { adapter, game, history, online, submitting, waiting } = get();
    if (!game || get().phase !== 'playing') return;
    if (game.dice) return; // already rolled — play the roll you have
    if (game.cubeOffer) return; // an unanswered double owns this turn
    if (online) {
      if (online.spectating) return; // watching, never playing
      // Server-authoritative: the roll endpoint rolls, stores, and fans out.
      // A pending cube offer blocks rolling — doubling replaced it, and the
      // offer must be answered first (server 409s too; this is the UX gate).
      if (submitting || waiting || game.cubeOffer) return;
      void (async () => {
        useBackgammonStore.setState({ submitting: true });
        try {
          adoptServerGame(await api.rollBackgammon(online.token, online.gameId));
        } catch (err) {
          useBackgammonStore.setState({
            submitting: false,
            onlineError: err instanceof ApiError ? err.message : 'roll-failed',
          });
        }
      })();
      return;
    }
    const rolled = adapter.roll(game);
    const candidates = adapter.legalTurns(rolled);
    playSfx('dice-roll');
    set({
      game: rolled,
      candidates,
      played: [],
      selected: null,
      targets: [],
      history: [...history, game],
    });
    if (candidates.length === 0) {
      // The roll cannot play. Show the dice for a beat, then pass — a kid
      // should SEE the dead roll, not watch the turn vanish.
      setTimeout(() => {
        const current = get();
        if (current.game === rolled && current.played.length === 0) {
          const passed = adapter.playTurn(rolled, { steps: [] });
          set({ game: passed, candidates: [], selected: null, targets: [] });
          void robotReply();
        }
      }, 900);
    }
  },

  offerDouble() {
    void cubeAction('double');
  },

  takeCube() {
    void cubeAction('take');
  },

  passCube() {
    void cubeAction('pass');
  },

  tap(endpoint: BackgammonEndpoint) {
    const state = get();
    const { game, candidates, played, selected } = state;
    if (!game?.dice || state.phase !== 'playing') return;
    if (state.online?.spectating) return;

    const nextStepOf = (turn: BackgammonTurn): BackgammonStep | undefined =>
      turn.steps[played.length];

    const selectSource = (source: BackgammonEndpoint): void => {
      const matches = candidates.filter((t) => nextStepOf(t)?.from === source);
      if (matches.length === 0) return;
      const destinations = matches
        .map((t) => nextStepOf(t)?.to)
        .filter((t): t is BackgammonEndpoint => t !== undefined);
      set({ selected: source, targets: [...new Set(destinations)] });
    };

    if (selected == null) {
      // Choosing a source: some candidate's next step must start here.
      selectSource(endpoint);
      return;
    }

    // Tapping the selected source again deselects.
    if (endpoint === selected) {
      set({ selected: null, targets: [] });
      return;
    }

    // Choosing a destination: narrow to candidates whose next step is from
    // `selected` to `endpoint`.
    const narrowed = candidates.filter((t) => {
      const next = nextStepOf(t);
      return next !== undefined && next.from === selected && next.to === endpoint;
    });
    if (narrowed.length === 0) {
      // Not a destination — treat the tap as a NEW source selection (fluid
      // for kids: no explicit cancel).
      selectSource(endpoint);
      return;
    }

    const diePips = narrowed[0]?.steps[played.length]?.pips;
    if (diePips === undefined) return;
    const playedNext: BackgammonStep[] = [
      ...played,
      { from: selected, to: endpoint, pips: diePips },
    ];
    const finished = narrowed.filter((t) => t.steps.length === playedNext.length);
    const finishedTurn = finished[0];
    if (finishedTurn) {
      commitTurn(finishedTurn);
      return;
    }
    // Partial play committed — keep narrowing. If exactly one candidate
    // remains, surface its next step's destinations straight away.
    playSfx(endpoint === 'off' ? 'bg-bear-off' : 'checker-clack');
    set({ played: playedNext, candidates: narrowed, selected: null, targets: [] });
    const remaining = narrowed[0];
    if (narrowed.length === 1 && remaining) {
      const next = remaining.steps[playedNext.length];
      if (next) set({ targets: [next.to], selected: next.from });
    }
  },

  undo() {
    const { history, phase, robotThinking, online } = get();
    if (online) return; // server-authoritative — no local undo
    if (phase !== 'playing' || robotThinking || history.length === 0) return;
    const previous = history[history.length - 1];
    if (!previous) return;
    set({
      game: previous,
      history: history.slice(0, -1),
      candidates: [],
      played: [],
      selected: null,
      targets: [],
      result: null,
    });
  },

  stop() {
    disconnectLive();
    robotWorker?.terminate();
    robotWorker = null;
    set({
      game: null,
      phase: 'idle',
      candidates: [],
      played: [],
      selected: null,
      targets: [],
      robotThinking: false,
      result: null,
      online: null,
      submitting: false,
      onlineError: null,
      waiting: false,
      clock: null,
    });
  },

  restart() {
    if (get().online) return; // no restarts in an online game
    const roller = get()._roller ?? Math.random;
    const adapter = new BackgammonAdapter(roller);
    const opening = rollOpening(roller);
    const game: BackgammonGame = {
      position: backgammonStartPosition(opening.turn),
      dice: opening.dice,
    };
    set({
      game,
      history: [],
      candidates: opening.turn === 'w' ? adapter.legalTurns(game) : [],
      played: [],
      selected: null,
      targets: [],
      phase: 'playing',
      robotThinking: false,
      result: null,
    });
    if (opening.turn === 'b') void robotReply();
  },
}));

/** Commit a fully-matched turn: play it, check the result, and hand the dice
 *  to the robot. Lives outside `create` because it re-enters the store. */
function commitTurn(turn: BackgammonTurn): void {
  const state = useBackgammonStore.getState();
  const { adapter, game } = state;
  if (!game) return;
  if (state.online) {
    // Online: submit the complete record; the response snapshot is the truth
    // (the live:move echo adopts the same state, so a double-adopt is a no-op).
    const { online } = state;
    if (!game.dice || state.submitting) return;
    const record: BackgammonTurnRecord = {
      dice: [...game.dice] as [number, number],
      steps: turn.steps.map((st) => ({ from: st.from, to: st.to, pips: st.pips })),
    };
    useBackgammonStore.setState({ submitting: true });
    void (async () => {
      try {
        adoptServerGame(await api.submitBackgammonTurn(online.token, online.gameId, record));
      } catch (err) {
        // The server refused (or the network dropped): resync from the game
        // truth instead of keeping a locally-narrowed phantom turn.
        useBackgammonStore.setState({
          submitting: false,
          onlineError: err instanceof ApiError ? err.message : 'turn-failed',
          candidates: [],
          played: [],
          selected: null,
          targets: [],
        });
      }
    })();
    return;
  }
  const next = adapter.playTurn(game, turn);
  const result = adapter.result(next);
  if (result) {
    useBackgammonStore.setState({
      game: { ...next, dice: null },
      candidates: [],
      played: [],
      selected: null,
      targets: [],
      phase: 'gameover',
      result,
    });
    return;
  }
  useBackgammonStore.setState({
    game: next,
    candidates: [],
    played: [],
    selected: null,
    targets: [],
    robotThinking: true,
  });
  void robotReply();
}

/** The robot's reply, on a delay so the kid sees the robot "think" — Easy
 *  (greedy), Clever (exact depth-1), Genius (bounded depth-2 probe, #208).
 *  The turn pick runs in a web worker when the environment has one — deep searches
 *  are synchronous CPU work and would freeze the screen — falling back to a
 *  direct call where workers don't exist (tests, exotic webviews). Stale
 *  replies are dropped by comparing the position the request was built
 *  from: any undo/restart/stop moves the store to a different position. */
let robotWorker: Worker | null = null;
let robotWorkerBroken = false;

function getRobotWorker(): Worker | null {
  if (robotWorkerBroken || typeof Worker === 'undefined') return null;
  if (!robotWorker) {
    try {
      robotWorker = new Worker(new URL('./backgammon-robot.worker.ts', import.meta.url), {
        type: 'module',
      });
    } catch {
      robotWorkerBroken = true;
      return null;
    }
  }
  return robotWorker;
}

function robotReply(): void {
  const ROBOT_THINK_MS = 700;
  setTimeout(() => {
    const { adapter, game } = useBackgammonStore.getState();
    if (!game || useBackgammonStore.getState().phase !== 'playing') return;
    // The opening roll is already on the board — play it, don't re-roll.
    const rolled = game.dice ? game : adapter.roll(game);
    if (!rolled.dice) return; // a finished game takes no dice
    const depth = useBackgammonStore.getState().robotDepth;
    const request: RobotTurnRequest = {
      position: rolled.position,
      dice: rolled.dice,
      depth,
    };
    const applyReply = (turn: ReturnType<typeof pickRobotTurn>): void => {
      const current = useBackgammonStore.getState();
      // Stale guard: only a reply for the position still on the board plays.
      if (!current.game || current.game.position !== rolled.position) return;
      if (current.phase !== 'playing') return;
      const next = current.adapter.playTurn(rolled, turn);
      const result = current.adapter.result(next);
      useBackgammonStore.setState({
        game: next,
        robotThinking: false,
        ...(result ? { phase: 'gameover' as const, result } : {}),
      });
    };
    const worker = getRobotWorker();
    if (worker) {
      requestRobotTurn(worker, request, applyReply);
      return;
    }
    applyReply(pickRobotTurn(request));
  }, ROBOT_THINK_MS);
}

/**
 * The i18n key for a finished game, from the HUMAN's seat. Plain wins reuse
 * the chess vocabulary (you-win / robot-wins); gammon and backgammon get
 * their own copy because "you won doubly!" is the payoff of carrying the
 * result kind across the protocol at all. Collapsing this to winner-only
 * would throw away exactly what #178 added.
 */
export function backgammonWinKey(result: BackgammonResult, viewer: Turn): string {
  const won = result.winner === viewer;
  if (result.kind === 'gammon') return won ? 'bg-gammon-you-win' : 'bg-gammon-robot-wins';
  if (result.kind === 'backgammon')
    return won ? 'bg-backgammon-you-win' : 'bg-backgammon-robot-wins';
  return won ? 'you-win' : 'robot-wins';
}

/**
 * May the human double now? Online only — the offline robot has no cube
 * policy (operator-ratified 2026-10-05), so offline the button never shows.
 * Mirrors the server's guards exactly: my turn, dice unrolled, no offer
 * pending, ownership, and under the 64 cap — the server 409/400s the same
 * cases, this is the button gate, not the authority.
 */
export function canDouble(state: BackgammonStore): boolean {
  const { game, online, phase, submitting, waiting } = state;
  if (!online || !game || phase !== 'playing' || submitting || waiting) return false;
  if (game.dice || game.cubeOffer) return false;
  if (game.position.turn !== online.side) return false;
  return canOfferDouble(cubeOf(game.cube), game.cubeOffer, online.side);
}

/** The opponent's double is awaiting MY take/pass (online only). */
export function pendingOfferAgainstMe(state: BackgammonStore): boolean {
  const { game, online } = state;
  if (!online || !game?.cubeOffer) return false;
  return game.cubeOffer.by !== online.side;
}

/**
 * Points at stake: the result's gammon multiplier (1/2/3) times the cube.
 * Display-only — v1 is casual, nothing persists (operator-ratified
 * 2026-10-05).
 */
export function backgammonStake(
  result: BackgammonResult,
  cube: BackgammonCube | undefined,
): number {
  return stakeMultiplier(result.kind) * cubeOf(cube).value;
}
